/**
 * "Count and Play" — counting 1-10 with one-to-one correspondence. N stars
 * appear; the child hits the drum N times while narration counts each hit;
 * upon reaching N, celebration and the next round.
 *
 * Gentle progression: targets grow 1→10 with some randomness so
 * two games are not identical.
 */

export interface CountAndPlayConfig {
  /** Maximum number (inclusive) — 10 in the MVP. */
  max: number;
  rounds: number;
  random?: () => number;
}

export interface CountAndPlayState {
  round: number;
  target: number;
  /** Hits made in the current round. */
  count: number;
  finished: boolean;
}

export interface CountAndPlayGame {
  state: () => CountAndPlayState;
  /**
   * One drum hit. Returns:
   * - "count": valid hit (narrate the number `state().count`)
   * - "round-complete": reached the target (celebrate; the UI calls nextRound)
   * - "finished": it was the last round
   * - "ignored": game already finished
   */
  hit: () => "count" | "round-complete" | "finished" | "ignored";
  /** Advances to the next round after the celebration. */
  nextRound: () => void;
}

export function createCountAndPlayGame(config: CountAndPlayConfig): CountAndPlayGame {
  const random = config.random ?? Math.random;

  /** Growing target with jitter: round 1 → 1-2, final round → near max. */
  const targetFor = (round: number): number => {
    const progress = (round - 1) / Math.max(1, config.rounds - 1);
    const base = 1 + Math.round(progress * (config.max - 1));
    const jitter = Math.round(random() * 2) - 1; // -1, 0 o +1
    return Math.min(config.max, Math.max(1, base + jitter));
  };

  const state: CountAndPlayState = {
    round: 1,
    target: targetFor(1),
    count: 0,
    finished: false,
  };
  let roundComplete = false;

  return {
    state: () => ({ ...state }),
    hit: () => {
      if (state.finished || roundComplete) {
        return "ignored";
      }
      state.count += 1;
      if (state.count < state.target) {
        return "count";
      }
      if (state.round >= config.rounds) {
        state.finished = true;
        return "finished";
      }
      roundComplete = true;
      return "round-complete";
    },
    nextRound: () => {
      if (!roundComplete || state.finished) {
        return;
      }
      roundComplete = false;
      state.round += 1;
      state.count = 0;
      state.target = targetFor(state.round);
    },
  };
}
